import React, { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';

// iOS starts its interactive back/forward swipe from roughly the outer 20px of
// the screen. Cover that strip and nothing more — every pixel here is a pixel
// the judging screens can't be touched on.
const EDGE_WIDTH_PX = 20;

/**
 * Swallows touches that begin on the left screen edge so iOS can't turn them
 * into a back-navigation gesture.
 *
 * Once a judge is on a scoring sheet the only way back is the marX logo:
 * an accidental swipe mid-round would drop marks or lose the round.
 *
 * `touch-action: none` tells WebKit the page owns any gesture starting here;
 * the non-passive touchstart/touchmove listeners are the belt-and-braces for
 * builds that only honour preventDefault(). Both are scoped to this strip, so
 * scrolling everywhere else is untouched — which is the whole reason we do not
 * block gestures globally (see the iNoBounce removal in index.tsx).
 */
const EdgeSwipeGuard: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const swallow = (event: TouchEvent) => {
      if (event.cancelable) {
        event.preventDefault();
      }
    };

    // React attaches touch handlers passively, so preventDefault() only works
    // from a listener we register ourselves.
    el.addEventListener('touchstart', swallow, { passive: false });
    el.addEventListener('touchmove', swallow, { passive: false });

    return () => {
      el.removeEventListener('touchstart', swallow);
      el.removeEventListener('touchmove', swallow);
    };
  }, []);

  return (
    <Box
      ref={ref}
      aria-hidden
      sx={{
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        width: `${EDGE_WIDTH_PX}px`,
        touchAction: 'none',
        // Above dialogs and snackbars: the strip only works if the touch lands
        // on it rather than on whatever is drawn over it.
        zIndex: (theme) => theme.zIndex.tooltip + 10
      }}
    />
  );
};

export default EdgeSwipeGuard;
